import { useEffect, useRef, useState } from 'react'

import { api } from '../api/client'
import type { MessagePage, Message } from '../api/types'

/**
 * Wie weit das Buch mit dem Einsammeln ist.
 *
 * **Es gibt keinen Gesamtwert, und das ist ehrlich so.** Der Server sagt nicht,
 * wie viele Nachrichten eine Reise hat — er sagt nur, ob es eine ältere Seite
 * gibt. Ein Fortschrittsbalken bräuchte ein Ganzes; hier steht deshalb eine
 * wachsende Zahl. Erfundene Prozente wären schlimmer als keine.
 */
export interface Sammelstand {
  /** Was bisher da ist, älteste zuerst. */
  nachrichten: Message[]
  /** Noch am Holen — solange darf nicht gedruckt werden. */
  laeuft: boolean
  /** Wie viele Seiten schon geholt sind. */
  seiten: number
  fehler: string | null
}

/**
 * Alle Nachrichten einer Reise holen, Seite für Seite.
 *
 * **Ein halbes Buch zu drucken wäre die stille Absage in Papierform.** Deshalb
 * sammelt dieser Haken bis zum Anfang der Reise und meldet erst dann `laeuft:
 * false`; der Druckknopf hängt daran.
 *
 * **Die Sichtbarkeitsregel steht nicht hier.** Ohne `with` liefert der Server
 * dem Absender ausschließlich `kind = 'broadcast'` (`Visibility::scope()`) —
 * das Buch ist damit vom Server her sauber, und hier wird nichts gefiltert und
 * nichts nachgeholfen. Mit `with` kommt der Verlauf mit genau einem Menschen.
 *
 * `before` ist die id der ältesten bisher geholten Nachricht; der Server gibt
 * sie als `next_before` zurück und `null`, wenn der Anfang erreicht ist.
 */
export function useAlleNachrichten(tripId: number, mitBenutzer?: number): Sammelstand {
  const [stand, setStand] = useState<Sammelstand>({
    nachrichten: [],
    laeuft: true,
    seiten: 0,
    fehler: null,
  })
  // **Ein Lauf je Reise und Gegenüber, und nicht zwei.** React ruft den Effekt
  // in der Entwicklung zweimal auf; ohne diese Sperre stünde jede Nachricht
  // doppelt im Buch.
  const laeuftFuer = useRef<string | null>(null)

  useEffect(() => {
    const schluessel = `${tripId}:${mitBenutzer ?? 0}`
    if (laeuftFuer.current === schluessel) return
    laeuftFuer.current = schluessel

    let abgebrochen = false
    const gesammelt: Message[] = []

    async function hole() {
      let before: number | null = null
      let seiten = 0
      try {
        for (;;) {
          const frage: Record<string, string | number> = { limit: 200 }
          if (mitBenutzer !== undefined) frage.with = mitBenutzer
          if (before !== null) frage.before = before

          const seite: MessagePage = await api.get<MessagePage>(
            `/api/trips/${tripId}/messages`,
            frage,
          )
          if (abgebrochen) return

          // Der Server liefert je Seite aufsteigend, die Seiten aber von neu
          // nach alt. Vorn anhängen ergibt die Reihenfolge des Buches.
          gesammelt.unshift(...seite.messages)
          seiten++
          setStand({ nachrichten: [...gesammelt], laeuft: true, seiten, fehler: null })

          if (seite.next_before === null) break
          before = seite.next_before
        }
        if (!abgebrochen) {
          setStand({ nachrichten: gesammelt, laeuft: false, seiten, fehler: null })
        }
      } catch (fehler) {
        if (abgebrochen) return
        // **Kein halbes Buch ohne Hinweis.** Was schon da ist, bleibt stehen —
        // aber der Knopf bleibt zu, und daneben steht, warum.
        setStand({
          nachrichten: gesammelt,
          laeuft: false,
          seiten,
          fehler: fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.',
        })
      }
    }

    void hole()
    return () => {
      abgebrochen = true
      laeuftFuer.current = null
    }
  }, [tripId, mitBenutzer])

  return stand
}
